import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEncompassTransaction,
  getEncompassTransactionResources,
  forceEncompassStatus,
  getEncompassLoan,
  uploadEncompassDocument,
  getEncompassActivities,
  getMeridianLinkLoan,
  updateMeridianLinkLoanFragment,
  submitMeridianLinkDocument,
  getMeridianLinkDocuments,
} from '../api/losProxy';

// ── Encompass Hooks ──

export function useEncompassTransaction(transactionId: string) {
  return useQuery({
    queryKey: ['los', 'encompass', 'transaction', transactionId],
    queryFn: () => getEncompassTransaction(transactionId),
    enabled: !!transactionId,
    retry: 1,
  });
}

export function useEncompassTransactionResources(transactionId: string) {
  return useQuery({
    queryKey: ['los', 'encompass', 'transaction', transactionId, 'resources'],
    queryFn: () => getEncompassTransactionResources(transactionId),
    enabled: !!transactionId,
    retry: 1,
  });
}

export function useForceEncompassStatus(transactionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: string; partnerStatus?: string; reason?: string }) =>
      forceEncompassStatus(transactionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['los', 'encompass', 'transaction', transactionId] });
    },
  });
}

export function useEncompassLoan(loanId: string) {
  return useQuery({
    queryKey: ['los', 'encompass', 'loan', loanId],
    queryFn: () => getEncompassLoan(loanId),
    enabled: !!loanId,
    retry: 1,
  });
}

export function useUploadEncompassDocument(transactionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { fileName: string; contentType: string; content: string }) =>
      uploadEncompassDocument(transactionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['los', 'encompass', 'transaction', transactionId, 'resources'] });
    },
  });
}

export function useEncompassActivities(correlationId: string) {
  return useQuery({
    queryKey: ['los', 'encompass', 'activities', correlationId],
    queryFn: () => getEncompassActivities(correlationId),
    enabled: !!correlationId,
    retry: 1,
  });
}

// ── MeridianLink Hooks ──

export function useMeridianLinkLoan(loanNumber: string) {
  return useQuery({
    queryKey: ['los', 'meridianlink', 'loan', loanNumber],
    queryFn: () => getMeridianLinkLoan(loanNumber),
    enabled: !!loanNumber,
    retry: 1,
  });
}

export function useUpdateMeridianLinkLoanFragment(loanNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { commands: any[] }) =>
      updateMeridianLinkLoanFragment(loanNumber, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['los', 'meridianlink', 'loan', loanNumber] });
    },
  });
}

export function useSubmitMeridianLinkDocument(appNumber: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; fileName: string; docGroup?: string; docCode?: string; content: string }) =>
      submitMeridianLinkDocument(appNumber, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['los', 'meridianlink', 'documents'] });
    },
  });
}

export function useMeridianLinkDocuments(loanNumber: string, params?: { appType?: string; documentTypes?: string }) {
  return useQuery({
    queryKey: ['los', 'meridianlink', 'documents', loanNumber, params],
    queryFn: () => getMeridianLinkDocuments(loanNumber, params),
    enabled: !!loanNumber,
    retry: 1,
  });
}
